import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api/axiosInstance";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";
import { SHIPPING_CHANGED_EVENT } from "../utils/shippingStatus";

/**
 * Shipping Settings (eventseasyadmin#299): the store's own courier account
 * or Manual / self-ship, with where it delivers and what it charges
 * (uniquegiftapi, #298 APIs 1, 2 and 4: GET / POST /shipping/settings,
 * POST /shipping/settings/test). Details can be tested before saving; saving
 * a courier tests it too. A saved API token is never shown again, only
 * "••••a7f3". Saving tells the top bar (SHIPPING_CHANGED_EVENT) so its pill
 * and banner update. Styling: AdminTheme.css .mtk-shipset-*.
 */

// Couriers not connected yet: listed so sellers know they're coming
const COMING_SOON = ["Blue Dart", "Ekart", "DTDC", "XpressBees", "Shadowfax", "India Post (Speed Post)", "Shree Maruti Courier", "Amazon Shipping", "Gati", "DHL Express", "FedEx"];

const PICKUP_FIELDS = [
    { key: "name", autoComplete: "name" },
    { key: "phone", autoComplete: "tel-national", inputMode: "tel", maxLength: 10 },
    { key: "address", autoComplete: "street-address", wide: true },
    { key: "city", autoComplete: "address-level2" },
    { key: "pincode", autoComplete: "postal-code", inputMode: "numeric", maxLength: 6 },
];

const emptyPickup = () => ({ name: "", phone: "", address: "", city: "", state: "", pincode: "" });

/** The badge and Connection row, from the saved settings */
export const settingsState = (settings) => {
    if (!settings || !settings.provider) return "not_set_up";
    if (settings.failed_at) return "failed";
    return settings.is_verified ? "ready" : "not_verified";
};

const formatChecked = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
};

const DELIVERY_DAYS = ["1-2", "2-3", "3-5", "5-7", "7-10"];

// Manual / self-ship areas and charges as the inputs hold them (amounts as text, "" for 0)
const manualForm = (m = {}) => ({
    deliver_all_india: m.deliver_all_india !== false,
    pincodes: m.pincodes || "",
    rate_in_state: m.rate_in_state ? String(m.rate_in_state) : "",
    rate_other: m.rate_other ? String(m.rate_other) : "",
    free_above: m.free_above ? String(m.free_above) : "",
    delivery_days: m.delivery_days || "3-5",
});

const formFrom = (settings, preset = "") => ({
    provider: settings.provider || preset,
    api_token: "",
    pickup_location_name: settings.pickup_location_name || "",
    pickup_address: { ...emptyPickup(), ...(settings.pickup_address || {}) },
    // On until the seller turns it off (the API's default too)
    cod_enabled: settings.cod_enabled !== false,
    manual: manualForm(settings.manual),
});

function ShippingSettings() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [settings, setSettings] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [form, setForm] = useState(null);
    const [replacing, setReplacing] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [errors, setErrors] = useState({});
    // { type: "success" | "danger", text } under the courier details, and at the bottom
    const [testResult, setTestResult] = useState(null);
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);

    const preset = { manual: "manual", courier: "delhivery" }[searchParams.get("mode")] || "";

    const fill = (result) => {
        setSettings(result);
        setForm(formFrom(result, preset));
        setReplacing(false);
        setShowToken(false);
    };

    const load = async () => {
        setLoadError(false);
        try {
            const res = await api.get("/shipping/settings");
            if (res.data && res.data.status === "success") fill(res.data.result);
            else setLoadError(true);
        } catch (e) {
            setLoadError(true);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const providers = (settings && settings.providers) || [];
    const available = providers.filter((p) => p.available);
    const chosen = form && providers.find((p) => p.key === form.provider);
    const isCourier = !!(chosen && chosen.courier);
    const savedToken = settings && settings.has_credentials && settings.provider === (form && form.provider) && !replacing;
    const state = settingsState(settings);

    const change = (fields) => {
        setForm((prev) => ({ ...prev, ...fields }));
        setErrors({});
        setTestResult(null);
        setMessage(null);
    };
    const changeManual = (fields) => {
        setForm((prev) => ({ ...prev, manual: { ...prev.manual, ...fields } }));
        setErrors({});
        setMessage(null);
    };
    const changePickup = (key, value) => {
        setForm((prev) => ({ ...prev, pickup_address: { ...prev.pickup_address, [key]: value } }));
        setErrors((prev) => ({ ...prev, [`pickup_address.${key}`]: undefined }));
    };

    const failure = (error, fallback) => {
        const data = (error && error.response && error.response.data) || {};
        if (data.field) setErrors({ [data.field]: data.message });
        return data.message || fallback;
    };

    const test = async () => {
        setBusy(true);
        setTestResult(null);
        try {
            const typed = form.api_token.trim();
            const res = await api.post("/shipping/settings/test", typed
                ? { provider: form.provider, api_token: typed, pickup_address: { pincode: form.pickup_address.pincode } }
                : {});
            const result = res.data.result || {};
            if (typed) {
                setTestResult(result.verified
                    ? { type: "success", text: t("shipping.settings.testOk", { provider: chosen.label }) }
                    : { type: "danger", text: t("shipping.settings.testRefused", { provider: chosen.label }) });
            } else {
                fill(result);
                window.dispatchEvent(new Event(SHIPPING_CHANGED_EVENT));
                setTestResult(result.is_verified && !result.failed_at
                    ? { type: "success", text: t("shipping.settings.connected", { provider: chosen.label, date: formatChecked(result.verified_at) }) }
                    : { type: "danger", text: t("shipping.settings.testRefused", { provider: chosen.label }) });
            }
        } catch (error) {
            setTestResult({ type: "danger", text: failure(error, t("shipping.settings.testFailed")) });
        } finally {
            setBusy(false);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.provider) {
            setErrors({ provider: t("shipping.settings.chooseProvider") });
            return;
        }
        setBusy(true);
        setMessage(null);
        setTestResult(null);
        try {
            const res = await api.post("/shipping/settings", {
                provider: form.provider,
                ...(isCourier && form.api_token.trim() ? { api_token: form.api_token.trim() } : {}),
                ...(isCourier ? { pickup_location_name: form.pickup_location_name } : {}),
                pickup_address: form.pickup_address,
                cod_enabled: form.cod_enabled,
                ...(isCourier ? {} : { manual: form.manual }),
            });
            const result = res.data.result;
            fill(result);
            window.dispatchEvent(new Event(SHIPPING_CHANGED_EVENT));
            const working = result.is_verified && !result.failed_at;
            if (isCourier) {
                setTestResult(working
                    ? { type: "success", text: t("shipping.settings.connected", { provider: chosen.label, date: formatChecked(result.verified_at) }) }
                    : { type: "danger", text: result.last_error ? t("shipping.settings.testRefused", { provider: chosen.label }) : t("shipping.settings.notWorking") });
            }
            setMessage(working
                ? {
                    type: result.warning ? "warning" : "success",
                    text: [isCourier ? t("shipping.settings.savedCourier", { provider: chosen.label }) : t("shipping.settings.savedManual"), result.warning].filter(Boolean).join(" "),
                }
                : { type: "danger", text: result.last_error || t("shipping.settings.notWorking") });
        } catch (error) {
            setMessage({ type: "danger", text: failure(error, t("shipping.settings.saveFailed")) });
        } finally {
            setBusy(false);
        }
    };

    const cancel = () => {
        setForm(formFrom(settings, preset));
        setReplacing(false);
        setErrors({});
        setTestResult(null);
        setMessage(null);
    };

    const fieldError = (field) => errors[field] && (
        <div className="invalid-feedback d-block" id={`${field}-error`}>{errors[field]}</div>
    );
    const described = (field, help) => (errors[field] ? `${field}-error` : help);

    // What checkout does now (the API's orders_blocked): orders still come in
    // while SHIPPING_REQUIRED is off or before the grace date, set up or not
    const taking = settings && settings.orders_blocked !== undefined ? !settings.orders_blocked : state === "ready";
    const required = !!(settings && settings.required);
    const BADGE_TONE = { not_set_up: "neutral", not_verified: "amber", failed: "red", ready: "green" };

    return (
        <div className="wrapper">
            <header>
                <NavigationBar />
            </header>

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="mtk-shipset-head">
                            <div>
                                <h1 className="m-0">{t("shipping.navLink")}</h1>
                                <p className="mtk-shipset-intro">{t("shipping.settings.intro")}</p>
                            </div>
                            {settings && (
                                <span className={`mtk-shipset-badge is-${BADGE_TONE[state]}`} data-testid="shipping-settings-state">
                                    {t(`shipping.settings.state.${state}`)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="content">
                    <div className="container-fluid">
                        {loadError && (
                            <div className="alert alert-warning" role="alert">
                                {t("shipping.settings.loadFailed")}{" "}
                                <button type="button" className="btn btn-link p-0 align-baseline" onClick={load}>{t("shipping.settings.tryAgain")}</button>
                            </div>
                        )}
                        {!settings && !loadError && <p className="text-muted" role="status">{t("shipping.settings.loading")}</p>}

                        {settings && form && (
                            <form onSubmit={save} noValidate>
                                <div className="mtk-shipset-layout">
                                    <div className="mtk-shipset-main">
                                        <section className="mtk-shipset-card" aria-labelledby="shipset-courier">
                                            <h2 id="shipset-courier">{t("shipping.settings.courierTitle")}</h2>
                                            <div className="mtk-shipset-body">
                                                <div className="form-group">
                                                    <label htmlFor="ship-provider">{t("shipping.settings.courierPartner")}</label>
                                                    <select
                                                        id="ship-provider"
                                                        className={`form-control mtk-shipset-select${errors.provider ? " is-invalid" : ""}`}
                                                        value={form.provider}
                                                        disabled={busy}
                                                        aria-describedby={described("provider", "ship-provider-help")}
                                                        onChange={(e) => { change({ provider: e.target.value, api_token: "" }); setReplacing(false); }}
                                                    >
                                                        <option value="">{t("shipping.settings.chooseCourier")}</option>
                                                        <optgroup label={t("shipping.settings.groupConnect")}>
                                                            {available.filter((p) => p.courier).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                                                        </optgroup>
                                                        <optgroup label={t("shipping.settings.groupSoon")}>
                                                            {COMING_SOON.map((name) => <option key={name} disabled>{name}</option>)}
                                                        </optgroup>
                                                        <optgroup label={t("shipping.settings.groupSelf")}>
                                                            {available.filter((p) => !p.courier).map((p) => (
                                                                <option key={p.key} value={p.key}>{t("shipping.settings.manualOption")}</option>
                                                            ))}
                                                        </optgroup>
                                                    </select>
                                                    {fieldError("provider") || (
                                                        <small id="ship-provider-help" className="form-text">{t("shipping.settings.courierHelp")}</small>
                                                    )}
                                                </div>

                                                {!form.provider && (
                                                    <div className="mtk-shipset-empty">
                                                        {t("shipping.settings.chooseHint")} <strong>{t("shipping.settings.noAccount")}</strong> {t("shipping.settings.noAccountHint")}
                                                    </div>
                                                )}

                                                {isCourier && (
                                                    <>
                                                        <div className="form-group">
                                                            <label htmlFor="ship-api-token">{t("shipping.settings.apiToken")}</label>
                                                            <div className="mtk-shipset-token">
                                                                {savedToken ? (
                                                                    <input id="ship-api-token" type="text" className="form-control is-saved" readOnly
                                                                        value={`••••••••••••${(settings.credentials_hint || "").replace(/•/g, "")}`}
                                                                        aria-describedby="ship-api-token-help" />
                                                                ) : (
                                                                    <input
                                                                        id="ship-api-token"
                                                                        type={showToken ? "text" : "password"}
                                                                        autoComplete="off"
                                                                        spellCheck={false}
                                                                        className={`form-control${errors.api_token || (testResult && testResult.type === "danger") ? " is-invalid" : ""}`}
                                                                        value={form.api_token}
                                                                        disabled={busy}
                                                                        aria-describedby={described("api_token", "ship-api-token-help")}
                                                                        onChange={(e) => change({ api_token: e.target.value })}
                                                                    />
                                                                )}
                                                                {savedToken ? (
                                                                    <button type="button" className="mtk-shipset-small-btn" onClick={() => { setReplacing(true); setTestResult(null); }}>
                                                                        {t("shipping.settings.replace")}
                                                                    </button>
                                                                ) : (
                                                                    <button type="button" className="mtk-shipset-small-btn" aria-pressed={showToken} onClick={() => setShowToken((v) => !v)}>
                                                                        {showToken ? t("shipping.settings.hide") : t("shipping.settings.show")}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {fieldError("api_token") || (
                                                                <small id="ship-api-token-help" className="form-text">
                                                                    {savedToken ? t("shipping.settings.tokenSavedHelp") : t("shipping.settings.tokenHelp", { provider: chosen.label })}
                                                                </small>
                                                            )}
                                                        </div>
                                                        <div className="form-group">
                                                            <label htmlFor="ship-location">{t("shipping.settings.pickupLocation", { provider: chosen.label })}</label>
                                                            <input
                                                                id="ship-location"
                                                                type="text"
                                                                className={`form-control${errors.pickup_location_name ? " is-invalid" : ""}`}
                                                                value={form.pickup_location_name}
                                                                maxLength={100}
                                                                disabled={busy}
                                                                aria-describedby={described("pickup_location_name", "ship-location-help")}
                                                                onChange={(e) => change({ pickup_location_name: e.target.value })}
                                                            />
                                                            {fieldError("pickup_location_name") || (
                                                                <small id="ship-location-help" className="form-text">{t("shipping.settings.pickupLocationHelp", { provider: chosen.label })}</small>
                                                            )}
                                                        </div>
                                                        <div className="mtk-shipset-test">
                                                            <button
                                                                type="button"
                                                                className="mtk-ship-btn mtk-ship-btn--outline"
                                                                onClick={test}
                                                                disabled={busy || (!form.api_token.trim() && !savedToken)}
                                                            >
                                                                {settings.is_verified && savedToken ? t("shipping.settings.testAgain") : t("shipping.settings.test")}
                                                            </button>
                                                            {!savedToken && <span className="form-text m-0">{t("shipping.settings.testHint")}</span>}
                                                        </div>
                                                        {testResult && (
                                                            <div className={`mtk-shipset-result is-${testResult.type}`} role={testResult.type === "danger" ? "alert" : "status"} data-testid="test-result">
                                                                {testResult.type === "danger"
                                                                    ? <><strong>{testResult.text}</strong> {t("shipping.settings.testRefusedHelp", { provider: chosen.label })}</>
                                                                    : <strong>{testResult.text}</strong>}
                                                            </div>
                                                        )}
                                                    </>
                                                )}

                                                {form.provider === "manual" && (
                                                    <>
                                                        <div className="mtk-shipset-note-box">{t("shipping.settings.manualExplain")}</div>
                                                        {/* Saved for #298's pincode check and shipping rates (API #6), which checkout doesn't apply yet */}
                                                        <p className="mtk-shipset-small" data-testid="manual-areas-note">{t("shipping.settings.manualAreasNote")}</p>
                                                        <fieldset className="mtk-shipset-fieldset" disabled={busy}>
                                                            <legend>{t("shipping.settings.whereDeliver")}</legend>
                                                            <label className="mtk-shipset-radio">
                                                                <input type="radio" name="ship-area" checked={form.manual.deliver_all_india}
                                                                    onChange={() => changeManual({ deliver_all_india: true })} />
                                                                {t("shipping.settings.allIndia")}
                                                            </label>
                                                            <label className="mtk-shipset-radio">
                                                                <input type="radio" name="ship-area" checked={!form.manual.deliver_all_india}
                                                                    onChange={() => changeManual({ deliver_all_india: false })} />
                                                                {t("shipping.settings.onlyPincodes")}
                                                            </label>
                                                            {!form.manual.deliver_all_india && (
                                                                <>
                                                                    <label htmlFor="ship-pincodes" className="sr-only">{t("shipping.settings.pincodesLabel")}</label>
                                                                    <textarea
                                                                        id="ship-pincodes"
                                                                        rows={3}
                                                                        className={`form-control mtk-shipset-textarea${errors["manual.pincodes"] ? " is-invalid" : ""}`}
                                                                        value={form.manual.pincodes}
                                                                        aria-describedby={described("manual.pincodes", "ship-pincodes-help")}
                                                                        onChange={(e) => changeManual({ pincodes: e.target.value })}
                                                                    />
                                                                    {fieldError("manual.pincodes") || (
                                                                        <small id="ship-pincodes-help" className="form-text">{t("shipping.settings.pincodesHelp")}</small>
                                                                    )}
                                                                </>
                                                            )}
                                                        </fieldset>
                                                        <div>
                                                            <div className="mtk-shipset-label">{t("shipping.settings.charges")}</div>
                                                            <div className="mtk-shipset-rates" role="table" aria-label={t("shipping.settings.charges")}>
                                                                <div className="mtk-shipset-rates__row is-head" role="row">
                                                                    <span role="columnheader">{t("shipping.settings.area")}</span>
                                                                    <span role="columnheader">{t("shipping.settings.chargePerOrder")}</span>
                                                                </div>
                                                                {[
                                                                    ["rate_in_state", t("shipping.settings.rateInState")],
                                                                    ["rate_other", t("shipping.settings.rateOther")],
                                                                    ["free_above", t("shipping.settings.freeAbove")],
                                                                ].map(([key, label]) => (
                                                                    <div className="mtk-shipset-rates__row" role="row" key={key}>
                                                                        <label role="cell" htmlFor={`ship-${key}`}>{label}</label>
                                                                        <span role="cell" className="mtk-shipset-rupee">
                                                                            <span aria-hidden="true">₹</span>
                                                                            <input
                                                                                id={`ship-${key}`}
                                                                                className={`form-control${errors[`manual.${key}`] ? " is-invalid" : ""}`}
                                                                                inputMode="decimal"
                                                                                value={form.manual[key]}
                                                                                onChange={(e) => changeManual({ [key]: e.target.value.replace(/[^\d.]/g, "") })}
                                                                            />
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {["rate_in_state", "rate_other", "free_above"].map((key) => fieldError(`manual.${key}`))}
                                                        </div>
                                                        <div className="form-group mtk-shipset-narrow">
                                                            <label htmlFor="ship-days">{t("shipping.settings.deliveryTime")}</label>
                                                            <select id="ship-days" className="form-control" value={form.manual.delivery_days}
                                                                onChange={(e) => changeManual({ delivery_days: e.target.value })}>
                                                                {DELIVERY_DAYS.map((d) => <option key={d} value={d}>{t("shipping.settings.days", { range: d.replace("-", "–") })}</option>)}
                                                            </select>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </section>

                                        <section className="mtk-shipset-card" aria-labelledby="shipset-pickup">
                                            <h2 id="shipset-pickup">{t("shipping.settings.pickupTitle")}</h2>
                                            <div className="mtk-shipset-body mtk-shipset-grid">
                                                {PICKUP_FIELDS.map((f) => {
                                                    const field = `pickup_address.${f.key}`;
                                                    return (
                                                        <div key={f.key} className={`form-group mb-0${f.wide ? " is-wide" : ""}`}>
                                                            <label htmlFor={`pickup-${f.key}`}>{t(`shipping.settings.pickup.${f.key}`)}</label>
                                                            <input
                                                                id={`pickup-${f.key}`}
                                                                type="text"
                                                                className={`form-control${errors[field] ? " is-invalid" : ""}`}
                                                                autoComplete={f.autoComplete}
                                                                inputMode={f.inputMode}
                                                                maxLength={f.maxLength}
                                                                disabled={busy}
                                                                aria-describedby={errors[field] ? `${field}-error` : undefined}
                                                                value={form.pickup_address[f.key]}
                                                                onChange={(e) => changePickup(f.key, e.target.value)}
                                                            />
                                                            {fieldError(field)}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </section>
                                    </div>

                                    <div className="mtk-shipset-side">
                                        <section className="mtk-shipset-card mtk-shipset-pad" aria-labelledby="shipset-cod">
                                            <div className="mtk-shipset-cod">
                                                <h2 id="shipset-cod">{t("shipping.settings.cod")}</h2>
                                                <label className="mtk-switch">
                                                    <input
                                                        type="checkbox"
                                                        role="switch"
                                                        checked={form.cod_enabled}
                                                        disabled={busy}
                                                        aria-label={t("shipping.settings.codAllow")}
                                                        onChange={(e) => change({ cod_enabled: e.target.checked })}
                                                    />
                                                    <span className="mtk-switch__track" aria-hidden="true"><span /></span>
                                                    <span className="mtk-switch__text" aria-hidden="true">{form.cod_enabled ? t("shipping.settings.on") : t("shipping.settings.off")}</span>
                                                </label>
                                            </div>
                                            <p className="mtk-shipset-small">{t("shipping.settings.codExplain")}</p>
                                        </section>

                                        <section className="mtk-shipset-card mtk-shipset-pad" aria-labelledby="shipset-status">
                                            <h2 id="shipset-status">{t("shipping.settings.statusTitle")}</h2>
                                            <dl className="mtk-shipset-status">
                                                <dt>{t("shipping.settings.statusCourier")}</dt>
                                                <dd>{settings.provider === "manual" ? t("shipping.settings.manualOption") : (providers.find((p) => p.key === settings.provider) || {}).label || t("shipping.settings.chooseCourier")}</dd>
                                                <dt>{t("shipping.settings.statusConnection")}</dt>
                                                <dd className={`is-${BADGE_TONE[state]}`}>{t(`shipping.settings.state.${state}`)}</dd>
                                                <dt>{t("shipping.settings.statusChecked")}</dt>
                                                <dd>{settings.provider === "manual" ? t("shipping.settings.notNeeded") : formatChecked(settings.verified_at) || "—"}</dd>
                                                <dt>{t("shipping.settings.statusTaking")}</dt>
                                                <dd className={taking ? "is-green" : "is-red"}>{taking ? t("shipping.settings.yes") : t("shipping.settings.paused")}</dd>
                                            </dl>
                                            {taking && required && <p className="mtk-shipset-small mtk-shipset-divider">{t("shipping.settings.changeWarning")}</p>}
                                        </section>

                                        <section className="mtk-shipset-card mtk-shipset-pad" aria-labelledby="shipset-help">
                                            <h2 id="shipset-help">{t("shipping.settings.helpTitle")}</h2>
                                            <details className="mtk-shipset-help">
                                                <summary>{t("shipping.settings.helpDelhivery")}</summary>
                                                <ol>
                                                    <li>{t("shipping.settings.helpDelhivery1")}</li>
                                                    <li>{t("shipping.settings.helpDelhivery2")}</li>
                                                    <li>{t("shipping.settings.helpDelhivery3")}</li>
                                                </ol>
                                            </details>
                                            <details className="mtk-shipset-help">
                                                <summary>{t("shipping.settings.helpManual")}</summary>
                                                <p>{t("shipping.settings.manualExplain")}</p>
                                            </details>
                                        </section>
                                    </div>
                                </div>

                                {message && (
                                    <div className={`alert alert-${message.type} mt-3 mb-0`} role={message.type === "danger" ? "alert" : "status"}>
                                        {message.text}
                                    </div>
                                )}

                                <div className="mtk-shipset-actions">
                                    <span className="mtk-shipset-small m-0">{taking ? "" : isCourier ? t("shipping.settings.ordersOnceVerified") : t("shipping.settings.ordersOnceSetUp")}</span>
                                    <button type="button" className="mtk-ship-btn mtk-ship-btn--plain mtk-ship-btn--lg" onClick={cancel} disabled={busy}>{t("shipping.settings.cancel")}</button>
                                    <button type="submit" className="mtk-ship-btn mtk-ship-btn--saffron mtk-ship-btn--lg" disabled={busy}>
                                        {busy ? t("shipping.settings.saving") : t("shipping.settings.save")}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}

export default ShippingSettings;
